import { useState, useEffect } from 'react';
import type { DailyQuest } from '../types/quest';
import { gamificationServiceApi } from '@/services/api';

// Daily challenges hook (fetches from Gamification Service)
export const useQuests = () => {
  const [quests, setQuests] = useState<DailyQuest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [timeRemainingMinutes, setTimeRemainingMinutes] = useState<number>(24 * 60);

  useEffect(() => {
    const loadQuests = async () => {
      setIsLoading(true);
      try {
        type ChallengeApiModel = {
          challengeId: number;
          title: string;
          rewardPoints: number;
          targetValue: number;
          currentProgress: number;
          status: 'completed' | 'in_progress' | 'not_started';
        };

        type DailyChallengesApiResponse =
          | {
              success: boolean;
              message: string;
              data: ChallengeApiModel[];
            }
          | {
              success: boolean;
              message: string;
              data: { items: ChallengeApiModel[]; time_remaining?: string };
            };

        const res = await gamificationServiceApi.get<DailyChallengesApiResponse>('/challenges/daily');

        // Support both legacy array response and new wrapped { items, time_remaining }
        const payload: any = res.data?.data;
        const items: ChallengeApiModel[] = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.items)
          ? payload.items
          : [];

        const timeRemainingStr: string | undefined =
          typeof payload?.time_remaining === 'string' ? payload.time_remaining : undefined;

        if (timeRemainingStr) {
          const trimmed = timeRemainingStr.trim().toLowerCase();
          if (trimmed.endsWith('m')) {
            const m = parseInt(trimmed.slice(0, -1), 10);
            if (!Number.isNaN(m)) setTimeRemainingMinutes(Math.max(0, m));
          } else if (trimmed.endsWith('h')) {
            const h = parseInt(trimmed.slice(0, -1), 10);
            if (!Number.isNaN(h)) setTimeRemainingMinutes(Math.max(0, h * 60));
          }
        }

        const mapped: DailyQuest[] = items.map((c) => ({
          id: String(c.challengeId),
          title: c.title,
          description: '',
          type: 'daily_streak',
          progress: c.currentProgress,
          target: c.targetValue,
          xpReward: c.rewardPoints,
          isCompleted: c.status === 'completed',
          icon: 'Trophy',
          difficulty: 'easy',
        }));

        setQuests(mapped);
      } catch (e) {
        console.error('Failed to load daily challenges', e);
        setQuests([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadQuests();
  }, []);

  const updateQuestProgress = (questId: string, newProgress: number) => {
    setQuests(prev => prev.map(quest => 
      quest.id === questId 
        ? { 
            ...quest, 
            progress: Math.min(newProgress, quest.target),
            isCompleted: newProgress >= quest.target 
          }
        : quest
    ));
  };

  const completeQuest = (questId: string) => {
    setQuests(prev => prev.map(quest => 
      quest.id === questId 
        ? { ...quest, isCompleted: true, progress: quest.target }
        : quest
    ));
  };

  const getTotalXP = () => {
    return quests.reduce((sum, quest) => 
      quest.isCompleted ? sum + quest.xpReward : sum, 0
    );
  };

  const getCompletedCount = () => {
    return quests.filter(quest => quest.isCompleted).length;
  };

  return {
    quests,
    isLoading,
    timeRemainingMinutes,
    updateQuestProgress,
    completeQuest,
    totalXP: getTotalXP(),
    completedCount: getCompletedCount(),
    totalQuests: quests.length
  };
};
